import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import {
  Button,
  Dialog,
  DialogActions as _DialogActions,
  DialogContent as _DialogContent,
  DialogTitle as _DialogTitle,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@material-ui/core';
import { createElement, FC, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import EditModificationRequestItem from '../../../../entity/EditModificationRequestItem';
import UploadingFile from '../../../../entity/UploadingFile';
import {
  DeliverableForCraetionDetailFragment,
  ModificationRequestItemForDetailFragment,
} from '../../../../generated/graphql';
import useInputComment, { inputCommentLimit } from '../../../../hooks/useInputComment';
import useInputDuration from '../../../../hooks/useInputDuration';
import theme from '../../../../theme';
import hmsToSeconds from '../../../../utility/hmsToSeconds';
import secondsToHms from '../../../../utility/secondsToHms';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import AspectRatioFixed from '../../primitive/AspectRatioFixed';
import ImageScreen from '../../primitive/ImageScreen';
import VideoScreen from '../../primitive/VideoScreen';
import FileList, { FileListItem, FileListUploadingItem } from '../FileList';
import _FileUploader from '../FileUploader';

interface Props {
  deliverable: DeliverableForCraetionDetailFragment;
  modificationRequestItem: ModificationRequestItemForDetailFragment;
  editModificationRequestItem: EditModificationRequestItem;
  files: Array<FileReference>;
  bucketId?: string;
  index: number;
  isOpen: boolean;
  onRequestClose: (e_: any) => void;
  onRequestEditAssetFiles: (files: UploadingFile[], bucketId: string) => void;
  onRemoveEditAssetFile: (fileId: string) => void;
  onSubmit: (
    index: number,
    editModificationRequestItem: EditModificationRequestItem,
    modificationRequestItemId: string,
    duration: string,
    comment: string,
  ) => void;
}

const EditModificationRequestDialog: FC<Props> = ({
  deliverable,
  modificationRequestItem,
  editModificationRequestItem,
  files,
  bucketId,
  index,
  isOpen,
  onRequestClose,
  onRequestEditAssetFiles,
  onRemoveEditAssetFile,
  onSubmit,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [inputComment, commentHandler] = useInputComment(modificationRequestItem.body);
  const [inputDuration, durationHandler] = useInputDuration(modificationRequestItem.metadata.duration, videoRef);

  const { uploadingAssetFiles, uploadingFilesProgress } = editModificationRequestItem;

  const assetIds = uploadingAssetFiles
    .map((file) => file.id)
    .concat(editModificationRequestItem.item ? editModificationRequestItem.item!.assetIds : []);

  const uploadedFiles = files.filter((file) => assetIds.includes(file.id));

  useEffect(() => {
    // * Using setTimeout for reference ref object.
    setTimeout(() => {
      commentHandler.setComment(modificationRequestItem.body);
      durationHandler.setVideoRef(videoRef);

      if (videoRef && videoRef.current && modificationRequestItem.metadata.duration) {
        videoRef.current.currentTime = hmsToSeconds(modificationRequestItem.metadata.duration);
      }
    }, 0);
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const itemNotUpdated =
    modificationRequestItem.body == inputComment.body &&
    modificationRequestItem.metadata.duration == inputDuration.duration &&
    JSON.stringify(modificationRequestItem.assetIds) === JSON.stringify(editModificationRequestItem.item?.assetIds);

  return (
    <Dialog open={isOpen} maxWidth="lg">
      <DialogTitle disableTypography>
        <Typography variant="h6">
          <FormattedMessage id="modification_request.edit_dialog.title" />
        </Typography>
      </DialogTitle>

      <DialogContent>
        <DeliverableScreen>
          {(deliverable.creative.file.mimeType.startsWith('video/') ||
            deliverable.creative.file.mimeType.startsWith('audio/')) && (
            <VideoOrImage aspectRatio={[4, 3]}>
              <VideoScreen
                src={deliverable.creative.file.url!}
                videoRef={videoRef}
                onSeek={(time: number) => {
                  const formattedTime = secondsToHms(time);
                  durationHandler.setDuration(formattedTime);
                }}
                onTimeUpdate={(time: number) => {
                  const formattedTime = secondsToHms(time);
                  durationHandler.setDuration(formattedTime);
                }}
                controls
                nodownload
              />
            </VideoOrImage>
          )}
          {deliverable.creative.file.mimeType.startsWith('image/') && (
            <VideoOrImage aspectRatio={[4, 3]}>
              <ImageScreen src={deliverable.creative.file.url} />
            </VideoOrImage>
          )}
        </DeliverableScreen>

        <Form>
          {deliverable.creative.file.mimeType.startsWith('video/') ? (
            <Durations>
              <FormControl>
                <InputDuration
                  label={<FormattedMessage id="modification_request.edit_dialog.input_duration.label" />}
                  value={inputDuration.duration}
                  type="time"
                  variant="outlined"
                  inputProps={{ step: '1' }}
                  error={inputDuration.error}
                  onChange={(e) => {
                    durationHandler.setDuration(e.target.value);
                    videoRef.current ? (videoRef.current.currentTime = hmsToSeconds(e.target.value)) : null;
                  }}
                  onFocus={() => {
                    videoRef.current ? videoRef.current.pause() : null;
                  }}
                />
                {inputDuration.error ? (
                  <InputCount>
                    <FormattedMessage id="modification_request.edit_dialog.input_duration.validation_error" />
                  </InputCount>
                ) : null}
              </FormControl>

              {videoRef.current ? (
                <Duration variant="body1" color="textSecondary">
                  {`/ ${secondsToHms(videoRef.current.duration)}`}
                </Duration>
              ) : null}
            </Durations>
          ) : null}

          <CommentForm error={inputComment.error}>
            <Comment
              label={<FormattedMessage id="modification_request.edit_dialog.input_comment.label" />}
              value={inputComment.body}
              variant="outlined"
              rows="4"
              rowsMax="8"
              multiline
              error={inputComment.error}
              onChange={(e: any) => commentHandler.setComment(e.target.value)}
            />
            <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
          </CommentForm>

          <FileList>
            {[
              ...uploadedFiles.map((file) => (
                <FileListItem
                  file={file}
                  key={file.id}
                  isVisibleSecondary={false}
                  onClickDeleteButton={() => onRemoveEditAssetFile(file.id)}
                />
              )),
              ...uploadingAssetFiles.map((file) => {
                const targetProgressFile = uploadingFilesProgress[file.id];

                return (
                  <FileListUploadingItem
                    file={file}
                    key={`uploading-${file.id}`}
                    progress={targetProgressFile ? targetProgressFile.progress : 0}
                    state="uploading"
                  />
                );
              }),
            ]}
          </FileList>

          {bucketId ? <FileUploader onSelectFiles={(_, files) => onRequestEditAssetFiles(files, bucketId)} /> : null}
        </Form>
      </DialogContent>

      <DialogActions>
        <Button
          color="default"
          variant="text"
          onClick={(e) => {
            commentHandler.setComment('');
            durationHandler.setDuration('00:00:00');
            onRequestClose(e);
          }}
        >
          <FormattedMessage id="reputation_dialog.cancel" />
        </Button>

        <Button
          variant="text"
          disabled={
            inputDuration.error ||
            inputComment.body.trim() === '' ||
            inputComment.error ||
            uploadingAssetFiles.length > 0 ||
            itemNotUpdated
          }
          onClick={() => {
            commentHandler.setComment('');
            durationHandler.setDuration('00:00:00');
            onSubmit(
              index,
              editModificationRequestItem,
              modificationRequestItem.id,
              inputDuration.duration,
              inputComment.body,
            );
          }}
        >
          <FormattedMessage id="modification_request.edit_dialog.submit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DialogTitle = styled(_DialogTitle)({
  borderBottom: `1px solid ${theme.palette.grey['200']}`,
});

const DialogContent = styled(_DialogContent)(
  gridLayoutContainer({
    columns: ['45%', '55%'],
    rows: ['auto'],
    areas: [['deliverable', 'form']],
  }),
  {
    width: 800,
    paddingTop: SPACING_UNIT,
  },
);

const DeliverableScreen = styled('div')(gridLayoutItem('deliverable', { alignSelf: 'flex-start' }), {
  marginRight: SPACING_REGULAR,
  marginBottom: SPACING_LARGE,
});

const VideoOrImage = styled(AspectRatioFixed)(gridLayoutItem('video', { alignSelf: 'flex-start' }), {
  marginBottom: SPACING_REGULAR,
});

const Form = styled('div')(gridLayoutItem('form'), {});

const Durations = styled('div')(flexibleLayoutContainer({ alignItems: 'center' }), {
  margin: `0 0 ${SPACING_UNIT * 3}px`,
});

const InputDuration = styled(TextField)({
  minWidth: 175,
});

const Duration = styled(Typography)({
  marginLeft: SPACING_REGULAR,
});

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_UNIT,
});

const Comment = styled(TextField)({
  width: '100%',
});

const InputCount = styled(FormHelperText)({
  textAlign: 'right',
});

const FileUploader = styled(_FileUploader)({
  height: 'auto',
});

const DialogActions = styled(_DialogActions)({
  borderTop: `1px solid ${theme.palette.grey['200']}`,
  marginTop: 0,
  paddingTop: SPACING_UNIT * 2,
  paddingBottom: SPACING_UNIT,
  height: 60,
});

export default EditModificationRequestDialog;
